import React, { useState, useEffect, useRef } from 'react';
import Navbar from './Navbar';
import CtaBanner from './CtaBanner';
import Footer from './Footer';
import { ArrowRight } from 'lucide-react';
import './ServicesPage.css';

const ServicesPage = ({ onNavigate }) => {
  // Services data taken directly from Fortuno "Services we provide.." section
  const servicesList = [
    {
      id: 'banking-loans',
      icon: 'https://cdn.prod.website-files.com/6671e04bfe71d2964a973baf/6671e04bfe71d2964a973c14_Banking%20Loans%20and%20Services.svg',
      tag: 'Financial Planning',
      tagType: 'blue',
      title: 'Banking Loans and Services',
      description:
        'Providing banking products and services such as savings accounts, loans, credit cards, and investment opportunities.',
    },
    {
      id: 'investment-portfolio',
      icon: 'https://cdn.prod.website-files.com/6671e04bfe71d2964a973baf/6671e04bfe71d2964a973c17_Investment%20Portfolio%20Management.svg',
      tag: 'Investment Management',
      tagType: 'green',
      title: 'Investment Portfolio Management',
      description:
        'Offering portfolio management services, investment advice, and investment products like mutual funds.',
    },
    {
      id: 'insurance-products',
      icon: 'https://cdn.prod.website-files.com/6671e04bfe71d2964a973baf/6671e04bfe71d2964a973c16_Insurance%20products%20and%20Services.svg',
      tag: 'Insurance Services',
      tagType: 'pink',
      title: 'Insurance products and Services',
      description:
        'Providing insurance products such as life insurance, health insurance, property and casualty insurance, and annuities.',
    },
    {
      id: 'creating-investment',
      icon: 'https://cdn.prod.website-files.com/6671e04bfe71d2964a973baf/6671e04bfe71d2964a973c13_Creating%20%26%20Managing%20Investment.svg',
      tag: 'Asset Management',
      tagType: 'purple',
      title: 'Creating & Managing Investment',
      description:
        'Offering strategic asset management, diversified capital allocation, and customized long-term investment advisory.',
    },
    {
      id: 'wealth-transfer',
      icon: 'https://cdn.prod.website-files.com/6671e04bfe71d2964a973baf/6671e04bfe71d2964a973c15_Strategies%20for%20Transferring%20Wealth.svg',
      tag: 'Wealth Transfer Services',
      tagType: 'orange',
      title: 'Strategies for Transferring Wealth',
      description:
        'Designing legacy transition frameworks, estate preservation strategies, family trusts, and generational financial security.',
    },
    {
      id: 'risk-advisory',
      icon: 'https://cdn.prod.website-files.com/6671e04bfe71d2964a973baf/6671e04bfe71d2964a973c14_Banking%20Loans%20and%20Services.svg',
      tag: 'Corporate & MSME Risk',
      tagType: 'cyan',
      title: 'Risk Advisory & Claims Assistance',
      description:
        'Dedicated corporate risk assessments, group health benefit structuring, and hassle-free claim settlement assistance.',
    },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(3);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Compute items per view dynamically
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setItemsPerView(1);
      } else if (window.innerWidth <= 1024) {
        setItemsPerView(2);
      } else {
        setItemsPerView(3);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIndex = Math.max(0, servicesList.length - itemsPerView);

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));
  };

  // Touch swipe support
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current - touchEndX.current > 50) {
      // Swiped Left
      handleNext();
    } else if (touchEndX.current - touchStartX.current > 50) {
      // Swiped Right
      handlePrev();
    }
  };

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="fortuno-services-page">
      {/* Top Navigation */}
      <Navbar isVisible={true} currentPage="services" onNavigate={onNavigate} />

      {/* Main Content: Fortuno "Services we provide.." Section */}
      <main className="fortuno-services-section">
        <div className="fortuno-container">
          
          {/* Header Row: Heading + Nav Arrows */}
          <div className="fortuno-header-row">
            <h1 className="fortuno-heading">Services we provide..</h1>

            <div className="fortuno-nav-arrows">
              <button
                className="fortuno-arrow-btn"
                onClick={handlePrev}
                disabled={currentIndex === 0}
                aria-label="Previous slide"
              >
                <svg className="arrow-svg-left" width="22" height="14" viewBox="0 0 22 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 7H1M1 7L7 1M1 7L7 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button
                className="fortuno-arrow-btn"
                onClick={handleNext}
                disabled={currentIndex >= maxIndex}
                aria-label="Next slide"
              >
                <svg className="arrow-svg-right" width="22" height="14" viewBox="0 0 22 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 7H21M21 7L15 1M21 7L15 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Slider Mask */}
          <div
            className="fortuno-slider-mask"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="fortuno-slider-track"
              style={{
                transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)`,
              }}
            >
              {servicesList.map((service) => (
                <div key={service.id} className="fortuno-card-slide">
                  <div
                    className="fortuno-card"
                    onClick={() => onNavigate && onNavigate('life-insurance')}
                    style={{ cursor: 'pointer' }}
                  >
                    {/* SVG Icon */}
                    <div className="fortuno-card-icon-wrap">
                      <img
                        src={service.icon}
                        alt={service.title}
                        className="fortuno-card-icon"
                        loading="lazy"
                      />
                    </div>

                    {/* Pill Tag */}
                    <div className={`fortuno-tag ${service.tagType}`}>
                      <span>{service.tag}</span>
                    </div>

                    {/* Title */}
                    <h3 className="fortuno-card-title">{service.title}</h3>

                    {/* Description */}
                    <p className="fortuno-card-desc">{service.description}</p>

                    {/* Learn More Link */}
                    <button
                      className="fortuno-learn-more"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onNavigate) onNavigate('life-insurance');
                      }}
                    >
                      <span>Learn More</span>
                      <ArrowRight size={18} className="arrow-icon" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pagination Dots */}
          <div className="fortuno-slider-dots">
            {Array.from({ length: maxIndex + 1 }).map((_, dotIdx) => (
              <button
                key={dotIdx}
                className={`fortuno-dot ${currentIndex === dotIdx ? 'active' : ''}`}
                onClick={() => setCurrentIndex(dotIdx)}
                aria-label={`Go to slide ${dotIdx + 1}`}
              />
            ))}
          </div>

        </div>
      </main>

      {/* Home Page CTA Banner */}
      <CtaBanner onNavigate={onNavigate} />

      {/* Home Page Footer */}
      <Footer onNavigate={onNavigate} />
    </div>
  );
};

export default ServicesPage;
